import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Footer from '../components/Footer';

const Billing = () => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, subRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/billing/subscription').catch(() => ({ data: { subscription: null } })),
      ]);

      setUser(userRes.data.user);
      setSubscription(subRes.data.subscription);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You\'ll continue to have access until the end of your billing period, then you\'ll be downgraded to the Free plan.'
    );

    if (!confirmed) return;

    try {
      setCancelLoading(true);
      const response = await api.post('/billing/cancel-subscription');

      toast.success(response.data.message);

      // Refresh subscription data
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setPortalLoading(true);
      const response = await api.post('/billing/create-portal-session');

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading billing information...</div>
      </div>
    );
  }

  const isPaidPlan = user?.plan && user.plan !== 'free';
  const isActive = subscription?.status === 'active';
  const willCancel = subscription?.cancelAtPeriodEnd;

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing information</p>
      </div>

      {/* Current Plan Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Current Plan</h2>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-indigo-600 capitalize">
                {user?.plan || 'Free'}
              </span>
              {willCancel && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                  Canceling Soon
                </span>
              )}
              {isActive && !willCancel && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  Active
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-2">
              {user?.monthlyQuota?.toLocaleString() || 0} requests per month
            </p>
          </div>

          <Link
            to="/pricing"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Change Plan
          </Link>
        </div>

        {/* Subscription Details */}
        {isPaidPlan && subscription && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Billing Cycle</p>
                <p className="font-medium text-gray-900">Monthly</p>
              </div>

              {subscription.currentPeriodEnd && (
                <div>
                  <p className="text-sm text-gray-500">
                    {willCancel ? 'Access Until' : 'Next Billing Date'}
                  </p>
                  <p className="font-medium text-gray-900">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium text-gray-900 capitalize">
                  {subscription.status}
                  {willCancel && ' (Canceling)'}
                </p>
              </div>
            </div>

            {willCancel && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-yellow-800">
                      Your subscription will be canceled on{' '}
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}.
                      You'll be downgraded to the Free plan with 100 requests per month.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manage Billing */}
      {isPaidPlan && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Manage Billing</h2>

          <div className="space-y-4">
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="w-full md:w-auto px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 transition-colors"
            >
              {portalLoading ? 'Opening...' : 'Update Payment Method & View Invoices'}
            </button>

            <p className="text-sm text-gray-600">
              Access the Stripe Customer Portal to update your payment method, download invoices, and view billing history.
            </p>

            {isActive && !willCancel && (
              <>
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                  >
                    {cancelLoading ? 'Processing...' : 'Cancel Subscription'}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    You'll keep access until the end of your current billing period
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Free Plan Info */}
      {!isPaidPlan && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <svg className="h-6 w-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                You're on the Free Plan
              </h3>
              <p className="text-blue-800 mb-4">
                Upgrade to unlock more requests, advanced features, and priority support.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                View Plans
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* FAQs */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Billing FAQs</h2>

        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-1">When will I be charged?</h3>
            <p className="text-sm text-gray-600">
              You'll be charged automatically at the start of each billing cycle. Your first charge happens immediately when you subscribe.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-1">What happens when I upgrade?</h3>
            <p className="text-sm text-gray-600">
              Upgrades happen instantly! You'll be prorated for the remaining time in your current billing cycle, and your usage counter resets.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-1">Can I downgrade my plan?</h3>
            <p className="text-sm text-gray-600">
              Yes! Visit the pricing page and select a lower-tier plan. The change takes effect at the end of your current billing period.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-1">What payment methods do you accept?</h3>
            <p className="text-sm text-gray-600">
              We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processor.
            </p>
          </div>
        </div>
      </div>
    </div>

    <Footer className="hidden md:block" />
    </>
  );
};

export default Billing;
